import React, { useState, useEffect, useMemo } from 'react';
import {
  Select, MenuItem, FormControl, InputLabel, CircularProgress, TextField
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AutoAwesome as SparklesIcon,
  Collections as GalleryIcon,
  CheckCircle as CheckIcon,
  Folder as FolderIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import AppHeader from '../components/common/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import GalleryModal, { GalleryItem } from '../components/stylista/GalleryModal';

// #T# MakeupPage — Makijaż
// Galeria twarzy: /stylista/faces/woman/female-faces.json
// Galeria stylów:  /stylista/makeup/female-makeup.json
// Backend: POST /api/makeup/generate — faceImage (file) lub faceImagePath (string) + hairstyleImagePath + prompt + model

interface MakeupStyle {
  name: string;
  path: string;
  category: { gender: string; type: string; style?: string };
  prompt: string;
  status: string;
}

interface FaceJson {
  name: string;
  path: string;
  category: { gender: string; age: string; ethnicity: string; hair_color: string };
  status: string;
}

const MODELS = [
  { value: 'strawberry-pro2', label: 'Strawberry Pro 2 (Gemini Flash)' },
  { value: 'strawberry-pro', label: 'Strawberry Pro (Gemini)' },
  { value: 'strawberry', label: 'Strawberry (Gemini)' },
  { value: 'blueberry-45', label: 'Blueberry 4.5 (SeedDream)' },
  { value: 'lemon-lux', label: 'Lemon Lux (Flux 2 Pro)' },
  { value: 'lemon-max', label: 'Lemon Max (Flux Max)' },
  { value: 'lemon-max2-edit', label: 'Lemon Max 2 Edit (Flux Max)' },
];

// Hairstyle matched per makeup type (image_2 reference)
const TYPE_TO_HAIRSTYLE: Record<string, string> = {
  'natural':     'Female_Layers_Long_Brunette_strawberry_pro.jpg',
  'everyday':    'Female_Bob_Classic_Blonde_strawberry_pro.jpg',
  'fresh-faced': 'Female_Waves_Beach_Brunette_strawberry_pro.jpg',
  'boy-beat':    'Female_Straight_Blunt_Blonde_strawberry_pro.jpg',
  'cleangirl':   'Female_Ponytail_High_Brunette_strawberry_pro.jpg',
  'glowing':     'Female_Layers_Long_Black_strawberry_pro.jpg',
  'soft':        'Female_Curls_Loose_Blonde_strawberry_pro.jpg',
  'matte':       'Female_Bob_Long_Brunette_strawberry_pro.jpg',
  'dewy':        'Female_Waves_Beach_Brunette_strawberry_pro.jpg',
};

const getHairstylePath = (type: string): string =>
  `/stylista/hairdresser/female/layers/${TYPE_TO_HAIRSTYLE[type] || 'Female_Layers_Long_Brunette_strawberry_pro.jpg'}`;

type GalleryType = 'faces' | 'makeup' | null;

const MakeupPage: React.FC = () => {
  const { isAdmin } = useAuth();

  // ---- Inputs ----
  const [faceFile, setFaceFile]   = useState<File | null>(null);
  const [facePreview, setFacePreview] = useState<string | null>(null);
  const [selectedFaceFromGallery, setSelectedFaceFromGallery] = useState<FaceJson | null>(null);
  const [selectedMakeupStyle, setSelectedMakeupStyle] = useState<MakeupStyle | null>(null);
  const [selectedModel, setSelectedModel] = useState('strawberry-pro2');
  const [promptOverride, setPromptOverride] = useState('');

  // ---- Gallery ----
  const [galleryFaces, setGalleryFaces]   = useState<GalleryItem[]>([]);
  const [galleryMakeup, setGalleryMakeup] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState<Record<string, boolean>>({});
  const [galleryError, setGalleryError]   = useState<string | null>(null);
  const [galleryType, setGalleryType]     = useState<GalleryType>(null);

  // ---- Generation ----
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [result, setResult]   = useState<any>(null);

  // ---- Face preview from File ----
  useEffect(() => {
    if (!faceFile) { setFacePreview(null); return; }
    const url = URL.createObjectURL(faceFile);
    setFacePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [faceFile]);

  // ---- Load galleries ----
  const loadGallery = async (kind: 'faces' | 'makeup') => {
    if (galleryLoading[kind]) return;
    setGalleryLoading(p => ({ ...p, [kind]: true }));
    setGalleryError(null);
    try {
      if (kind === 'faces') {
        const [femaleResp, maleResp] = await Promise.all([
          fetch('/stylista/faces/woman/female-faces.json'),
          fetch('/stylista/faces/man/male-faces.json'),
        ]);
        const [femaleData, maleData] = await Promise.all([femaleResp.json(), maleResp.json()]);
        const allFaces: FaceJson[] = [
          ...(Array.isArray(femaleData) ? femaleData : []),
          ...(Array.isArray(maleData) ? maleData : []),
        ].filter(f => f.status === 'done');
        setGalleryFaces(allFaces.map(f => ({
          id: f.name,
          name: f.name.replace(/_/g, ' '),
          thumbnail_url: `/stylista/faces/${f.path}/${f.name}_strawberry_pro_miniature.jpg`,
          image_url:     `/stylista/faces/${f.path}/${f.name}_strawberry_pro.jpg`,
          group:    f.category.ethnicity,
          gender:   f.category.gender,
          age:      f.category.age,
          hair_color: f.category.hair_color,
        })));
      } else {
        const resp = await fetch('/stylista/makeup/female-makeup.json');
        const data: MakeupStyle[] = await resp.json();
        setGalleryMakeup(data.map(s => ({
          id:    s.name,
          name:  s.name.replace('Female_Makeup_', '').replace(/_/g, ' '),
          thumbnail_url: `/stylista/makeup/${s.path}/${s.name}_strawberry_pro_miniature.jpg`,
          image_url:     `/stylista/makeup/${s.path}/${s.name}_strawberry_pro.jpg`,
          group: s.category.type,
        })));
      }
    } catch {
      setGalleryError('Nie udało się wczytać galerii.');
    } finally {
      setGalleryLoading(p => ({ ...p, [kind]: false }));
    }
  };

  const openGallery = (type: GalleryType) => {
    if (type) {
      const notLoaded = type === 'faces' ? galleryFaces.length === 0 : galleryMakeup.length === 0;
      if (notLoaded && !galleryLoading[type]) loadGallery(type);
    }
    setGalleryType(type);
  };

  // Fetch face as File (same-origin nginx proxy)
  const pickFaceFromGallery = async (item: GalleryItem) => {
    const src = item.image_url || '';
    if (!src) { setGalleryError('Brak URL obrazu.'); return; }
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const safeName = (item.name || 'face').replace(/\s+/g, '-');
      setFaceFile(new File([blob], `${safeName}.jpg`, { type: 'image/jpeg' }));
      setSelectedFaceFromGallery(null);
    } catch (e: any) {
      setGalleryError(`Nie mogę pobrać obrazu: ${e.message}`);
    }
  };

  const handleGallerySelect = (item: GalleryItem) => {
    if (galleryType === 'faces') {
      pickFaceFromGallery(item);
    } else if (galleryType === 'makeup') {
      setSelectedMakeupStyle(null);
      setSelectedMakeupStyleFromGallery(item);
    }
    setGalleryType(null);
  };

  // We store the full MakeupStyle separately for makeup gallery selection
  const [selectedMakeupStyleFromGallery, setSelectedMakeupStyleFromGallery] = useState<GalleryItem | null>(null);

  // Resolve full MakeupStyle object when selected from gallery
  const [makeupStylesRaw, setMakeupStylesRaw] = useState<MakeupStyle[]>([]);
  useEffect(() => {
    fetch('/stylista/makeup/female-makeup.json')
      .then(r => r.json())
      .then((data: MakeupStyle[]) => setMakeupStylesRaw(data))
      .catch(() => {});
  }, []);

  // Resolved makeup style (from gallery or direct selection)
  const resolvedMakeupStyle: MakeupStyle | null = useMemo(() => {
    if (selectedMakeupStyle) return selectedMakeupStyle;
    if (selectedMakeupStyleFromGallery) {
      return makeupStylesRaw.find(s => s.name === selectedMakeupStyleFromGallery.id) || null;
    }
    return null;
  }, [selectedMakeupStyle, selectedMakeupStyleFromGallery, makeupStylesRaw]);

  // ---- Prompt preview for admin ----
  const effectivePrompt = useMemo(() => {
    if (promptOverride.trim()) return promptOverride.trim();
    if (!resolvedMakeupStyle) return '';
    return resolvedMakeupStyle.prompt || '';
  }, [promptOverride, resolvedMakeupStyle]);

  const hairstylePath = resolvedMakeupStyle ? getHairstylePath(resolvedMakeupStyle.category.type) : null;

  const currentFaceDisplay = facePreview || (selectedFaceFromGallery
    ? `/stylista/faces/${selectedFaceFromGallery.path}/${selectedFaceFromGallery.name}.jpg`
    : null);

  // ---- Generate ----
  const handleGenerate = async () => {
    if (!resolvedMakeupStyle) { setError('Wybierz styl makijażu'); return; }
    if (!faceFile && !selectedFaceFromGallery) { setError('Dodaj zdjęcie twarzy lub wybierz z galerii'); return; }

    setLoading(true); setError(null); setResult(null);
    try {
      const formData = new FormData();

      if (faceFile) {
        formData.append('faceImage', faceFile);
      } else if (selectedFaceFromGallery) {
        formData.append('faceImagePath', `/stylista/faces/${selectedFaceFromGallery.path}/${selectedFaceFromGallery.name}.jpg`);
      }

      if (hairstylePath) formData.append('hairstyleImagePath', hairstylePath);

      formData.append('prompt', effectivePrompt || resolvedMakeupStyle.prompt);
      formData.append('model', selectedModel);
      formData.append('resolution', '2k');
      formData.append('aspect_ratio', '3:4');

      const response = await apiClient.post('makeup/generate', formData, {
        headers: { 'Content-Type': null },
        timeout: 180000,
      });

      if (response.data.success) setResult(response.data);
      else setError(response.data.message || 'Błąd generowania');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Błąd generowania');
    } finally {
      setLoading(false);
    }
  };

  const clearFace = () => { setFaceFile(null); setSelectedFaceFromGallery(null); };
  const clearMakeup = () => { setSelectedMakeupStyle(null); setSelectedMakeupStyleFromGallery(null); };

  const canGenerate = !!resolvedMakeupStyle && (!!faceFile || !!selectedFaceFromGallery);

  return (
    <>
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">💄 Makeup Studio</h1>
          <p className="text-sm text-gray-500 mt-1">Wybierz styl makijażu i zobacz efekt na twarzy</p>
        </div>

        {/* Error / gallery error */}
        {error && (
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-600"><CloseIcon fontSize="small" /></button>
          </div>
        )}
        {galleryError && (
          <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 mb-4 text-sm text-yellow-700">
            <span>{galleryError}</span>
            <button onClick={() => setGalleryError(null)} className="ml-3 text-yellow-400 hover:text-yellow-600"><CloseIcon fontSize="small" /></button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Inputs */}
          <div className="flex flex-col gap-4">

            {/* Step 1: Face */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="font-semibold text-gray-700 mb-3">1. Twarz</h2>
              {currentFaceDisplay ? (
                <div className="relative">
                  <img
                    src={currentFaceDisplay} alt="Selected face"
                    className="w-full max-h-72 object-contain rounded-lg border-2 border-green-400"
                  />
                  <button
                    onClick={clearFace}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-colors min-h-[44px]">
                      <UploadIcon fontSize="small" />
                      Wgraj zdjęcie
                      <input type="file" className="hidden" accept="image/*"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFaceFile(f); setSelectedFaceFromGallery(null); } }} />
                    </label>
                    <label className="flex items-center justify-center gap-1 px-4 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-colors min-h-[44px]">
                      <CameraIcon fontSize="small" />
                      <input type="file" className="hidden" accept="image/*" capture="user"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFaceFile(f); setSelectedFaceFromGallery(null); } }} />
                    </label>
                  </div>
                  <span className="text-xs text-gray-400 text-center">lub</span>
                  <button
                    onClick={() => openGallery('faces')}
                    className="flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors min-h-[44px]"
                  >
                    <GalleryIcon fontSize="small" />
                    Galeria twarzy ({galleryFaces.length > 0 ? galleryFaces.length : '...'})
                  </button>
                </div>
              )}
            </div>

            {/* Step 2: Makeup Style */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="font-semibold text-gray-700 mb-3">2. Styl makijażu</h2>
              {resolvedMakeupStyle ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <img
                    src={`/stylista/makeup/${resolvedMakeupStyle.path}/${resolvedMakeupStyle.name}_strawberry_pro.jpg`}
                    alt={resolvedMakeupStyle.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="px-3 py-2 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">
                        {resolvedMakeupStyle.name.replace('Female_Makeup_', '').replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-gray-400">Typ: {resolvedMakeupStyle.category.type}</p>
                    </div>
                    <button onClick={clearMakeup} className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => openGallery('makeup')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors min-h-[44px]"
                >
                  <FolderIcon fontSize="small" />
                  Przeglądaj style makijażu
                </button>
              )}
            </div>

            {/* Step 3: Model (admin only) */}
            {isAdmin && (
              <div className="border border-gray-200 rounded-xl p-4">
                <h2 className="font-semibold text-gray-700 mb-3">3. Model AI</h2>
                <FormControl fullWidth size="small">
                  <InputLabel>Model</InputLabel>
                  <Select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} label="Model">
                    {MODELS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </div>
            )}

            {/* Admin: reference images preview */}
            {isAdmin && (resolvedMakeupStyle || hairstylePath) && (
              <div className="border border-blue-100 bg-blue-50/30 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Zdjęcia referencyjne (admin)</p>
                <div className="flex gap-3 flex-wrap">
                  {currentFaceDisplay && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Image 1: Twarz</p>
                      <img src={currentFaceDisplay} alt="face ref" className="h-20 w-20 object-cover rounded border border-gray-200" />
                    </div>
                  )}
                  {hairstylePath && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Image 2: Fryzura</p>
                      <img src={hairstylePath} alt="hairstyle ref" className="h-20 w-20 object-cover rounded border border-gray-200"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Admin: prompt */}
            {isAdmin && (
              <div className="border border-blue-100 bg-blue-50/30 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Prompt (admin)</p>
                <TextField
                  fullWidth multiline minRows={4}
                  value={promptOverride !== '' ? promptOverride : effectivePrompt}
                  onChange={(e) => setPromptOverride(e.target.value)}
                  placeholder="Prompt ze stylu makijażu (możesz nadpisać)"
                  helperText="Automatycznie z wybranego stylu. Edytuj aby nadpisać."
                  sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                />
              </div>
            )}

            {/* Generate */}
            <div className="sticky bottom-0 z-20 -mx-4 px-4 py-3 bg-white/95 backdrop-blur-sm border-t border-gray-100 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0">
              <button
                onClick={handleGenerate}
                disabled={loading || !canGenerate}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors min-h-[52px]"
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : <SparklesIcon fontSize="small" />}
                {loading ? 'Generowanie...' : 'Generuj makijaż'}
              </button>
            </div>
          </div>

          {/* Right: Result */}
          <div className="border border-gray-200 rounded-xl p-4">
            <h2 className="font-semibold text-gray-700 mb-3">Wynik</h2>
            {result ? (
              <div>
                {result.preview_files?.map((file: string, idx: number) => (
                  <img key={idx} src={file} alt={`Result ${idx + 1}`}
                    className="w-full rounded-xl mb-3 border-2 border-green-400" />
                ))}
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700">
                  <CheckIcon fontSize="small" />
                  <span>{result.message}</span>
                </div>
                {isAdmin && result.metadata?.stylista_prompt && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1">Prompt wysłany do modelu:</p>
                    <pre className="text-xs font-mono bg-blue-50/40 border border-blue-100 rounded p-2 whitespace-pre-wrap break-words text-gray-600">
                      {result.metadata.stylista_prompt}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-sm text-gray-400">Wynik pojawi się tutaj</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* GalleryModal — faces */}
      <GalleryModal
        open={galleryType === 'faces'}
        onClose={() => setGalleryType(null)}
        onSelect={handleGallerySelect}
        items={galleryFaces}
        loading={!!galleryLoading['faces']}
        title="Galeria twarzy"
        defaultGroup="european"
        defaultGender="woman"
        defaultAge="20plus"
        groupLabel="Pochodzenie:"
      />

      {/* GalleryModal — makeup styles (portrait 3:4) */}
      <GalleryModal
        open={galleryType === 'makeup'}
        onClose={() => setGalleryType(null)}
        onSelect={handleGallerySelect}
        items={galleryMakeup}
        loading={!!galleryLoading['makeup']}
        title="Style makijażu"
        groupLabel="Kategoria:"
        imageObjectFit="contain"
        imageHeight={180}
      />
    </>
  );
};

export default MakeupPage;
